import { ReferencedEntry } from 'contentful';
import React from 'react';

import { Assembly, ContentType } from 'src/api/contentful/contentTypes.cloverdotcom';
import { AccordionModule } from 'src/components/AccordionModule/AccordionModule';
import { ArticleModule } from 'src/components/ArticleModule/ArticleModule';
import { BannerContentModule } from 'src/components/BannerContentModule/BannerContentModule';
import { YellowHighlightBannerModule } from 'src/components/BannerModule/YellowHighlightBannerModule';
import { BundleSummaryModule } from 'src/components/BundleSummaryModule/BundleSummaryModule';
import { BusinessResourceGrid } from 'src/components/BusinessResourceGrid/BusinessResourceGrid';
import { CardModuleContainer as CardModule } from 'src/components/CardModule/CardModule';
import { CarouselContainer as CarouselModule } from 'src/components/CarouselModule/CarouselModule';
import { ComponentAssembly } from 'src/components/ComponentAssembly/ComponentAssembly';
import { ControlledScrollModule } from 'src/components/ControlledScrollModule/ControlledScrollModule';
import { DynamicAssembly } from 'src/components/DynamicAssembly/DynamicAssembly';
import { applyEntryStyleAttributes } from 'src/components/EntryWrapper/EntryStyleWrapper';
import { FaqModule } from 'src/components/FaqModule/FaqModule';
import { FeatureComparisonModule } from 'src/components/FeatureComparisonModule/FeatureComparisonModule';
import { FloatingTextModule } from 'src/components/FloatingTextModule/FloatingTextModule';
import { GridModule } from 'src/components/GridModule/GridModule';
import { HeroModule } from 'src/components/HeroModule/HeroModule';
import { IconFeatureGrid } from 'src/components/IconFeatureGrid/IconFeatureGrid';
import { ImageTextModule } from 'src/components/ImageTextModule/ImageTextModule';
import { LeadGenFormModule } from 'src/components/LeadGenFormModule/LeadGenFormModule';
import { MultiPanelModule } from 'src/components/MultiPanelModule/MultiPanelModule';
import { PlainTextModule } from 'src/components/PlainTextModule/PlainTextModule';
import { PricingDetailModule } from 'src/components/PricingDetailModule/PricingDetailModule';
import { ProductSystemsCatalog } from 'src/components/ProductSystemsCatalog/ProductSystemsCatalog';
import { ProductTileModule } from 'src/components/ProductTileModule/ProductTileModule';
import { QuotesModule } from 'src/components/Quotes/QuotesModule';
import { SalesContactModule } from 'src/components/SalesContactModule/SalesContactModule';
import { SideBySideModule } from 'src/components/SideBySideModule/SideBySideModule';
import { SingleTestimonialModule } from 'src/components/SingleTestimonialModule/SingleTestimonialModule';
import { SolutionPickerModule } from 'src/components/SolutionPickerModule/SolutionPickerModule';
import { StickyHeader } from 'src/components/StickyHeader/StickyHeader';
import { TabsModule } from 'src/components/TabsModule/TabsModule';
import { TestimonialModule } from 'src/components/Testimonial/TestimonialModule';
import { TextMediaModule } from 'src/components/TextMediaModule/TextMediaModule';
import { getContentType } from 'src/utils/CloverdotcomEntryUtil';
import { ContentfulModuleWrapper } from '../ContentfulModuleWrapper/ContentfulModuleWrapper';

export interface IModuleWrapperProps extends React.AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<Assembly>;
}

type ModuleMap = Record<ContentType, React.ComponentType<IModuleWrapperProps>>;
export const MODULE_MAP: Readonly<Partial<ModuleMap>> = {
  [ContentType.CARD_ASSEMBLY]: CardModule,
  [ContentType.GRID_ASSEMBLY]: GridModule,
  [ContentType.BANNER_ASSEMBLY]: YellowHighlightBannerModule,
  [ContentType.BUNDLE_SUMMARY_MODULE]: BundleSummaryModule,
  [ContentType.ACCORDION_ASSEMBLY]: AccordionModule,
  [ContentType.PLAIN_TEXT_MODULE]: PlainTextModule,
  [ContentType.MEDIA_MODULE]: TextMediaModule,
  [ContentType.FAQ_MODULE]: FaqModule,
  [ContentType.FLOATING_TEXT_ASSEMBLY]: FloatingTextModule,
  [ContentType.CONTROLLED_SCROLL_ASSEMBLY]: ControlledScrollModule,
  [ContentType.CAROUSEL_ASSEMBLY]: CarouselModule,
  [ContentType.TABS_ASSEMBLY]: TabsModule,
  [ContentType.ARTICLE_ASSEMBLY]: ArticleModule,
  [ContentType.QUOTES_MODULE]: QuotesModule,
  [ContentType.TESTIMONIAL_MODULE]: TestimonialModule,
  [ContentType.PRODUCT_TILE_ASSEMBLY]: ProductTileModule,
  [ContentType.DYNAMIC_ASSEMBLY]: DynamicAssembly,
  [ContentType.FEATURE_COMPARISON_ASSEMBLY]: FeatureComparisonModule,
  [ContentType.STICKY_HEADER_ASSEMBLY]: StickyHeader,
  [ContentType.LEAD_GEN_FORM_MODULE]: LeadGenFormModule,
  [ContentType.SIDE_BY_SIDE_MODULE]: SideBySideModule,
  [ContentType.MULTIPANEL_MODULE]: MultiPanelModule,
  [ContentType.PRICING_DETAIL_MODULE]: PricingDetailModule,
  [ContentType.SALES_CONTACT_MODULE]: SalesContactModule,
  [ContentType.SOLUTION_PICKER_MODULE]: SolutionPickerModule,
  [ContentType.HERO_MODULE]: ContentfulModuleWrapper(HeroModule),
  [ContentType.ICON_FEATURE_GRID]: ContentfulModuleWrapper(IconFeatureGrid),
  [ContentType.BANNER_MODULE]: ContentfulModuleWrapper(BannerContentModule),
  [ContentType.SINGLE_TESTIMONIAL_MODULE]: ContentfulModuleWrapper(SingleTestimonialModule),
  [ContentType.COMPONENT_ASSEMBLY]: ContentfulModuleWrapper(ComponentAssembly),
  [ContentType.IMAGE_TEXT_MODULE]: ContentfulModuleWrapper(ImageTextModule),
  [ContentType.BUSINESS_RESOURCE_GRID]: ContentfulModuleWrapper(BusinessResourceGrid),
  [ContentType.PRODUCT_SYSTEMS_CATALOG]: ProductSystemsCatalog,
};

const ModuleWrapper: React.FC<IModuleWrapperProps> = props => {
  const contentType = getContentType(props.entry);

  if (!contentType) return null;

  const component = MODULE_MAP[contentType];

  if (!component) return null;

  return React.createElement(component, props);
};

const wrappedComponent = applyEntryStyleAttributes(ModuleWrapper);
export { wrappedComponent as ModuleWrapper };
